import Feature from 'ol/Feature';
import type Map from 'ol/Map';
import type Geometry from 'ol/geom/Geometry';
import type Draw from 'ol/interaction/Draw';
import Snap from 'ol/interaction/Snap';
import type { EventsKey } from 'ol/events';
import { unByKey } from 'ol/Observable';
import VectorSource from 'ol/source/Vector';

export const DIFFERENCE_DRAW_ASSISTANCE = Object.freeze({
  snapPixelTolerance: 10,
  snapEnabledByDefault: true,
  traceEnabledByDefault: false,
});

export interface DifferenceDrawAssistState {
  readonly active: boolean;
  readonly snapEnabled: boolean;
  readonly traceEnabled: boolean;
  readonly snapped: boolean;
  readonly snappedCoordinate: readonly number[] | null;
}

/** Owns the temporary, target-only source used by OpenLayers Snap and Draw tracing. */
export class DifferenceDrawingAssist {
  readonly source: VectorSource;
  readonly snap: Snap;
  private readonly targetFeature: Feature<Geometry>;
  private readonly listenerKeys: EventsKey[];
  private map: Map | null = null;
  private draw: Draw | null = null;
  private snapEnabled: boolean = DIFFERENCE_DRAW_ASSISTANCE.snapEnabledByDefault;
  private traceEnabled: boolean = DIFFERENCE_DRAW_ASSISTANCE.traceEnabledByDefault;
  private snapped = false;
  private snappedCoordinate: number[] | null = null;

  constructor(targetGeometry: Geometry, private readonly stateChanged: (state: DifferenceDrawAssistState) => void = () => undefined) {
    this.targetFeature = new Feature(targetGeometry.clone());
    this.source = new VectorSource({ features: [this.targetFeature], wrapX: false });
    this.snap = new Snap({
      source: this.source,
      edge: true,
      vertex: true,
      intersection: false,
      pixelTolerance: DIFFERENCE_DRAW_ASSISTANCE.snapPixelTolerance,
    });
    this.listenerKeys = [
      this.snap.on('snap', (event) => this.setSnapped(true, event.vertex)),
      this.snap.on('unsnap', () => this.setSnapped(false, null)),
    ];
  }

  attach(map: Map, draw: Draw): void {
    this.map = map;
    this.draw = draw;
    this.draw.setTrace(this.traceEnabled);
    this.snap.setActive(this.snapEnabled);
    // Added after Draw so Snap handles browser events first (interactions run in reverse order).
    this.map.addInteraction(this.snap);
    this.emit(true);
  }

  updateTargetGeometry(targetGeometry: Geometry): void {
    this.targetFeature.setGeometry(targetGeometry.clone());
  }

  setSnapEnabled(enabled: boolean): void {
    this.snapEnabled = enabled;
    this.snap.setActive(enabled);
    if (!enabled) {
      this.snapped = false;
      this.snappedCoordinate = null;
    }
    this.emit(Boolean(this.map));
  }

  setTraceEnabled(enabled: boolean): void {
    this.traceEnabled = enabled;
    this.draw?.setTrace(enabled);
    this.emit(Boolean(this.map));
  }

  getState(): DifferenceDrawAssistState {
    return {
      active: Boolean(this.map),
      snapEnabled: this.snapEnabled,
      traceEnabled: this.traceEnabled,
      snapped: this.snapped,
      snappedCoordinate: this.snappedCoordinate ? [...this.snappedCoordinate] : null,
    };
  }

  dispose(): void {
    if (this.map) this.map.removeInteraction(this.snap);
    this.draw?.setTrace(false);
    this.map = null;
    this.draw = null;
    this.snapped = false;
    this.snappedCoordinate = null;
    unByKey(this.listenerKeys);
    this.listenerKeys.length = 0;
    this.source.clear(true);
    this.emit(false);
  }

  private setSnapped(snapped: boolean, coordinate: readonly number[] | null): void {
    if (!this.snapEnabled) return;
    this.snapped = snapped;
    this.snappedCoordinate = snapped && coordinate ? [...coordinate] : null;
    this.emit(Boolean(this.map));
  }

  private emit(active: boolean): void {
    this.stateChanged({
      active,
      snapEnabled: this.snapEnabled,
      traceEnabled: this.traceEnabled,
      snapped: active && this.snapped,
      snappedCoordinate: active && this.snappedCoordinate ? [...this.snappedCoordinate] : null,
    });
  }
}
